import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Grid,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import {
  DiagnosisConsumptionReadDTO,
  DiagnosisConsumptionCreateDTO,
  IDiagnosisConsumptionCreateDTO,
  DiagnosisEnergyVectorsReadDTO,
  EnergyVectorsReadDTO,
  ConsumptionType,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { ActionButton, UnitOfMeasurementDropdown } from '../../common';
import { invalidateConsumptionTotalsQueries } from '../../../hooks';

interface EditConsumptionDialogProps {
  open: boolean;
  onClose: () => void;
  consumption: DiagnosisConsumptionReadDTO | null;
  energyVectorId: string;
  diagnosisId?: string; // ID della diagnosi per invalidare le query corrette
  existingConsumptions?: DiagnosisConsumptionReadDTO[]; // Per calcolare l'index
  genericEnergyVectors?: EnergyVectorsReadDTO[]; // Vettori energetici generici passati dallo step
}

export const EditConsumptionDialog: React.FC<EditConsumptionDialogProps> = ({
  open,
  onClose,
  consumption,
  energyVectorId,
  diagnosisId,
  existingConsumptions = [],
  genericEnergyVectors = [],
}) => {
  const queryClient = useQueryClient();

  // Carica il vettore energetico della diagnosi
  const { data: diagnosisEnergyVector } = useQuery<DiagnosisEnergyVectorsReadDTO>(
    ['diagnosis-energy-vector', energyVectorId],
    () => apiClient.get_item_diagnosis_energy_vectors__item_id__get(energyVectorId),
    { enabled: !!energyVectorId && open }
  );

  // Ottieni original_energy_vector_id dal vettore energetico della diagnosi
  const originalEnergyVectorId = (diagnosisEnergyVector as any)?.original_energy_vector_id;

  // Trova il vettore energetico generico per ottenere l'unità di misura di default
  const genericVector = React.useMemo(() => {
    if (!originalEnergyVectorId || !genericEnergyVectors.length) return null;
    return genericEnergyVectors.find(gv => gv.id === originalEnergyVectorId);
  }, [originalEnergyVectorId, genericEnergyVectors]);

  const defaultUnitOfMeasurement =
    genericVector?.unit_of_measurement || diagnosisEnergyVector?.unit_of_measurement;

  // Calcola l'index per un nuovo consumo
  const calculateNewIndex = React.useCallback((): number => {
    if (consumption) {
      // Se è un update, mantieni l'index esistente se presente
      return (consumption as any).index ?? existingConsumptions.length + 1;
    }
    // Per un nuovo consumo: index = numero consumi esistenti + 1
    return existingConsumptions.length + 1;
  }, [consumption, existingConsumptions]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DiagnosisConsumptionCreateDTO & { unit_of_measurement?: string }>({
    defaultValues: consumption
      ? {
          code: consumption.code ?? '',
          diagnosis_energy_vector_id: energyVectorId,
          type: consumption.type as ConsumptionType,
          unit_of_measurement: (consumption as any).unit_of_measurement ?? defaultUnitOfMeasurement,
        }
      : {
          diagnosis_energy_vector_id: energyVectorId,
          code: '',
          type: ConsumptionType.Total,
          unit_of_measurement: defaultUnitOfMeasurement,
        },
  });

  // Usa un ref per tracciare se il dialog è stato appena aperto
  const isInitialMount = React.useRef(true);
  const previousOpen = React.useRef(open);

  React.useEffect(() => {
    // Reset solo quando il dialog viene aperto
    if (open && (isInitialMount.current || previousOpen.current !== open)) {
      const newIndex = calculateNewIndex();

      if (consumption) {
        reset({
          code: consumption.code ?? '',
          diagnosis_energy_vector_id: energyVectorId,
          type: consumption.type as ConsumptionType,
          unit_of_measurement: (consumption as any).unit_of_measurement ?? defaultUnitOfMeasurement,
        });
      } else {
        // Reset per nuovo consumo
        reset({
          diagnosis_energy_vector_id: energyVectorId,
          code: '',
          type: ConsumptionType.Total,
          unit_of_measurement: defaultUnitOfMeasurement,
        });
      }
      isInitialMount.current = false;
    }
    previousOpen.current = open;
  }, [consumption, energyVectorId, reset, open, calculateNewIndex, defaultUnitOfMeasurement]);

  const createMutation = useMutation(
    async (data: DiagnosisConsumptionCreateDTO) => {
      return await apiClient.create_item_diagnosis_consumption__post(data);
    },
    {
      onSuccess: () => {
        // Invalida tutte le query relative ai consumi
        queryClient.invalidateQueries({ queryKey: [`all-consumptions-${energyVectorId}`] });
        if (diagnosisId) {
          queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
        queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
          invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
        }
        toast.success('Consumo creato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore nella creazione del consumo');
      },
    }
  );

  const updateMutation = useMutation(
    async (data: DiagnosisConsumptionCreateDTO) => {
      if (!consumption?.id) {
        throw new Error('ID consumo mancante');
      }
      return await apiClient.update_item_diagnosis_consumption__item_id__put(consumption.id, data);
    },
    {
      onSuccess: () => {
        // Invalida tutte le query relative ai consumi per ricaricare la tabella
        queryClient.invalidateQueries({ queryKey: [`all-consumptions-${energyVectorId}`] });
        if (diagnosisId) {
          queryClient.invalidateQueries({ queryKey: [`all-consumptions-${diagnosisId}`] });
        queryClient.invalidateQueries({ queryKey: [`all-consumption-data-${diagnosisId}`] });
          invalidateConsumptionTotalsQueries(queryClient, diagnosisId);
        }
        toast.success('Consumo aggiornato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento del consumo");
      },
    }
  );

  const onSubmit = (data: DiagnosisConsumptionCreateDTO & { unit_of_measurement?: string }) => {
    const submitData: IDiagnosisConsumptionCreateDTO = {
      ...data,
      unit_of_measurement: data.unit_of_measurement ?? defaultUnitOfMeasurement,
    };

    if (consumption) {
      updateMutation.mutate(submitData as DiagnosisConsumptionCreateDTO);
    } else {
      // Calculate index for new consumption (set to last position)
      const maxIndex = existingConsumptions.reduce((max, c) => Math.max(max, (c as any).index ?? 0), 0);
      submitData.index = maxIndex + 1;
      createMutation.mutate(submitData as DiagnosisConsumptionCreateDTO);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset();
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{consumption ? 'Modifica consumo' : 'Nuovo consumo'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codice"
                {...register('code', { required: 'Il codice è obbligatorio' })}
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="type"
                control={control}
                rules={{ required: 'Il tipo è obbligatorio' }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Tipo</InputLabel>
                    <Select {...field} label="Tipo">
                      <MenuItem value={ConsumptionType.Total}>Totale</MenuItem>
                      <MenuItem value={ConsumptionType.Withdrawn}>Prelevata</MenuItem>
                      <MenuItem value={ConsumptionType.Self_consumed}>Autoconsumata</MenuItem>
                    </Select>
                    {fieldState.error && (
                      <FormHelperText>{fieldState.error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {defaultUnitOfMeasurement && (
                <Controller
                  name="unit_of_measurement"
                  control={control}
                  render={({ field, fieldState }) => (
                    <UnitOfMeasurementDropdown
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      defaultUnitOfMeasurement={defaultUnitOfMeasurement}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip={'Salva'}
              loading={isSubmitting}
              disabled={isSubmitting || !isDirty}
              buttonType="submit"
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
